import { ChangeEvent, FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEnterSubmit } from "@libs/zapi-ui/custom";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea
} from "@libs/zapi-ui/ds/components/input-group";
import { ArrowUpIcon } from "@phosphor-icons/react";
import { useCommentStore } from "../model/store/commentsStore";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { generateStubComment } from "../lib/generateStubComment";
import { useScopedTimeout } from "@/zapi-frontend/shared/hooks/useScopedTimeout";

type TCommentInputFieldProps = {
  isLoading: boolean;
  userId: number;
  firstName: string;
  lastName: string;
  onSubmit: () => void;
};

export function CommentInputField(props: TCommentInputFieldProps) {
  const { isLoading = false, userId, firstName, lastName, onSubmit } = props;
  const { t } = useTranslation("projectHome");

  const { createComment } = useCommentStore();
  const { activeChapter } = useChapterStore();
  const { selectedProject } = useProjectStore();
  const { commentsUpdated } = useEditorStore();
  const { runTimeout } = useScopedTimeout();
  const [message, setMessage] = useState("");

  const projectId = selectedProject?.id;
  const chapterId = activeChapter?.id.toString() || "";

  function handleInputChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setMessage(e.target.value);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!userId || !message.trim() || !projectId || !chapterId) return;

    setMessage("");

    runTimeout(() => {
      onSubmit();
    }, 0);
    await createComment({
      projectId,
      chapterId,
      content: message.trim(),
      stubComment: generateStubComment({ userId, firstName, lastName, content: message.trim() })
    });
    commentsUpdated?.();
  }

  return (
    <form onSubmit={handleSubmit} className="shrink-0 border-t border-foreground/5 p-3">
      <InputGroup className="rounded-2xl bg-background">
        <InputGroupTextarea
          value={message}
          onChange={handleInputChange}
          onKeyDown={(event) => {
            if (isEnterSubmit(event)) {
              event.preventDefault();
              event.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder={t("comments.addComment")}
          aria-label={t("comments.addComment")}
          disabled={isLoading}
          rows={1}
          data-testid="comment-input"
          className="typo-body-sm max-h-32 min-h-10 px-3.5 pt-3"
        />
        <InputGroupAddon align="block-end" className="justify-end gap-1 pb-2">
          {!!message && (
            <InputGroupButton
              variant="ghost"
              size="xs"
              className="rounded-full text-muted-foreground"
              data-testid="comment-input-cancel-button"
              onClick={() => setMessage("")}
            >
              {t("comments.cancel")}
            </InputGroupButton>
          )}
          <InputGroupButton
            type="submit"
            variant="default"
            size="icon-sm"
            className="rounded-full"
            aria-label={t("comments.postComment")}
            disabled={isLoading || !message.trim()}
            data-testid="post-comment-button"
          >
            <ArrowUpIcon weight="bold" />
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
    </form>
  );
}
